import { useQueryClient } from '@tanstack/react-query';
import { useOptimisticMutation, type Variables } from '../../../hooks/use-optimistic-mutation';
import { PriceListError } from '../errors';
import type { PriceListApiResponse, PriceListEdit } from '../types';
import { sortLists } from '.';
import { QK_ALL_PRICE_LISTS, QK_GET_PRICE_LIST, QK_PAGINATED_PRICE_LISTS } from './use-price-lists';
import { usePriceListsApi } from './use-price-lists-api';

export function useUpdatePriceList() {
  const { priceListsApi } = usePriceListsApi();
  const queryClient = useQueryClient();

  async function updateList({ slug, priceList }: { slug: string; priceList: PriceListEdit }) {
    let updated: PriceListApiResponse;
    try {
      updated = await priceListsApi.priceList(slug).update(priceList);
    } catch {
      throw new PriceListError('update_failed');
    }

    // The mutation's queryKey only covers the paginated list; the edit form reads
    // ['price-list', slug]. Seed it from the authoritative PATCH response so re-opening
    // the form shows fresh data rather than the stale pre-edit value.
    queryClient.setQueryData(QK_GET_PRICE_LIST(slug), updated);
  }

  return useOptimisticMutation<
    PriceListApiResponse[],
    Variables<typeof updateList>,
    PriceListError<'update_failed'>
  >({
    queryClient,
    queryKey: QK_PAGINATED_PRICE_LISTS(),
    invalidateKeys: [QK_ALL_PRICE_LISTS()],
    mutationFn: updateList,
    // Sorted by name to match backend
    optimisticUpdate: (prev, newList) =>
      sortLists(
        prev?.map((list) =>
          list.slug === newList.slug ? { ...list, ...newList.priceList } : list,
        ),
      ),
  });
}
